'use client'

import React from 'react'
import {
  AlertTriangle,
  Search,
  Eye,
  Bot,
  Wrench,
  Layers,
  Code,
  Rocket,
  Users,
  CheckCircle,
} from 'lucide-react'

/**
 * Origin Story Timeline Diagram
 * Visual journey from AI invisibility to building Radar
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

const phases = [
  {
    date: 'Oct 2025',
    title: 'GSC wake-up call',
    description:
      'Google Search Console flagged indexing failures. Pages missing, 404 errors piling up, organic traffic down 33%.',
    icon: AlertTriangle,
    color: '#F90B8A',
    commits: 5,
  },
  {
    date: 'Oct 2025',
    title: 'Asked AI about ourselves',
    description:
      'ChatGPT, Perplexity, Gemini: zero citations. One model described services we never offered.',
    icon: Search,
    color: '#F90B8A',
    commits: 3,
  },
  {
    date: 'Nov 2025',
    title: 'AI was misrepresenting us',
    description:
      'Commit deef381: 30 files rebuilt. Structured data sending conflicting signals. LLMs filling gaps with hallucinations.',
    icon: Eye,
    color: '#FDC304',
    commits: 8,
  },
  {
    date: 'Nov 2025',
    title: 'Fixed bot access',
    description:
      'robots.txt had no AI rules. llms.txt did not exist. Added crawler policies, hardened bot variants.',
    icon: Bot,
    color: '#FDC304',
    commits: 6,
  },
  {
    date: 'Feb 2026',
    title: 'Cleaned structured data',
    description:
      'Removed fake ratings, duplicate schemas. Built 18-entity knowledge graph. GSC-driven content optimization.',
    icon: Wrench,
    color: '#8b5cf6',
    commits: 12,
  },
  {
    date: 'Feb 2026',
    title: 'Overhauled 21 posts',
    description:
      'StatBlocks, BlogTables, FAQ schemas, speakable markup. Every post made independently citable by AI.',
    icon: Layers,
    color: '#8b5cf6',
    commits: 15,
  },
  {
    date: 'Feb 2026',
    title: 'Automated into tools',
    description:
      'AI Crawl Checker, Citation Tracker, Robots Analyzer. Each tool born from a manual audit step we were tired of repeating.',
    icon: Code,
    color: '#008dcc',
    commits: 10,
  },
  {
    date: 'Mar 2026',
    title: 'Radar is born',
    description:
      'All 13 tools in one parallel scan. Cross-tool conflict detection. LLM Answer Diff. 60-second full audit.',
    icon: Rocket,
    color: '#008dcc',
    commits: 20,
  },
  {
    date: 'Apr 2026',
    title: '50 users validated it',
    description:
      '10+ minute sessions. Return visits. Waitlist demand after token expiry. The problem was not unique to us.',
    icon: Users,
    color: '#22c55e',
    commits: 50,
  },
]

export function OriginStoryTimeline() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='flex items-center gap-2 mb-6'>
        <CheckCircle className='w-4 h-4 text-muted-foreground' />
        <span className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
          The Radar Origin Story
        </span>
      </div>

      <div className='relative'>
        {/* Vertical line */}
        <div className='absolute left-[19px] top-3 bottom-3 w-px bg-[var(--border)]' />

        <div className='space-y-1'>
          {phases.map((phase, index) => {
            const Icon = phase.icon
            const isLast = index === phases.length - 1

            return (
              <div key={index} className='relative flex gap-4'>
                {/* Icon circle */}
                <div
                  className='relative z-10 flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center'
                  style={{ backgroundColor: `${phase.color}15` }}
                >
                  <Icon className='w-4 h-4' style={{ color: phase.color }} />
                </div>

                {/* Content */}
                <div className={`flex-1 pb-5 ${isLast ? 'pb-0' : ''}`}>
                  <div className='flex items-center gap-2 mb-1'>
                    <span
                      className='text-[10px] font-bold uppercase tracking-wider'
                      style={{ color: phase.color }}
                    >
                      {phase.date}
                    </span>
                  </div>
                  <h4 className='text-sm font-semibold text-foreground mb-1'>
                    {phase.title}
                  </h4>
                  <p className='text-xs text-foreground/60 leading-relaxed'>
                    {phase.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Summary bar */}
      <div className='mt-6 pt-4 border-t border-border/60 flex items-center justify-between'>
        <div className='text-center'>
          <p className='text-lg font-bold' style={{ color: '#008dcc' }}>
            6 months
          </p>
          <p className='text-[10px] text-muted-foreground'>
            Oct 2025 to Apr 2026
          </p>
        </div>
        <div className='text-center'>
          <p className='text-lg font-bold' style={{ color: '#8b5cf6' }}>
            516 commits
          </p>
          <p className='text-[10px] text-muted-foreground'>
            Every fix documented
          </p>
        </div>
        <div className='text-center'>
          <p className='text-lg font-bold' style={{ color: '#22c55e' }}>
            13 tools
          </p>
          <p className='text-[10px] text-muted-foreground'>Now in Radar</p>
        </div>
      </div>
    </div>
  )
}
